import '@availity/block-ui/src/Loader.css';
import '@availity/block-ui/src/BlockUi.css';
import { Chart, registerables } from 'chart.js';
import { Component } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { Container } from 'semantic-ui-react';
import { loginSuccess } from './actions/actions';
import LoggingIn from './components/shared/LoggingIn';
import Login from './components/shared/Login';
import LogOut from './components/shared/LogOut';
import PrivateRoute from './components/shared/PrivateRoute';
import { defaultRoutePath, groupedRoutes, loggingInPathPrefix, loginPathPrefix, logoutPathPrefix } from './components/shared/routes';
import { TransitIqSidebar } from './components/shared/sidebar';
import TransitIqRootRoute from './components/shared/TransitIqRootRoute';
import UserMenu from './components/shared/UserMenu';
import configureStore from './store/configureStore';
import { isValidUser, saveCurrentUser } from './utilities/user';
import userManager from './utilities/userManager';

const store = configureStore();
Chart.register(...registerables);

store.subscribe(() => {
    const { user } = store.getState();
    saveCurrentUser(user);
});

const { user } = store.getState();
if (isValidUser(user)) {
    store.dispatch(loginSuccess(user));
}

class AppInternal extends Component {

    private unsubscribe: any = null;
    private isSubscribed = false;

    state = {
        currentUser: null,
    };

    componentDidMount(): void {
        console.log('App - componentDidMount');

        this.unsubscribe = store.subscribe(() => {
            const { user } = store.getState();
            this.setState({
                currentUser: user,
            });
        });
        this.subscribeToUserManagerEvents();
    }

    componentWillUnmount(): void {
        console.log('App - componentWillUnmount');

        // Unsubscribe from store
        this.unsubscribe && this.unsubscribe();

        // Unsubscribe from userManager events
        userManager.events.removeAccessTokenExpired(this.handleTokenExpired);
    }

    private subscribeToUserManagerEvents() {
        if (this.isSubscribed) {
            console.log('Already subscribed to userManager events, skipping');
            return;
        }
        console.log('Subscribing to userManager events');
        this.isSubscribed = true;

        // Add event handlers for token expiration, token expired, and silent renewal errors
        userManager.events.addAccessTokenExpired(this.handleTokenExpired);
    }

    private isOnLoginPage = (): boolean => {
        return window.location.pathname.startsWith(loginPathPrefix) ||
            window.location.pathname.startsWith(loggingInPathPrefix);
    };

    private handleTokenEvent = (eventName: string, error?: Error) => {
        console.log(`${eventName} triggered: ${window.location.pathname}`);

        // Don't handle events if we're already on login or logging page
        if (this.isOnLoginPage()) {
            console.log(`On login/logging page, skipping ${eventName} handler`);
            return;
        }

        if (error) {
            console.error(`${eventName}:`, error);
        } else {
            console.log(`${eventName} event triggered`);
        }

        this.forceRelogin();
    };

    private handleTokenExpired = () => {
        this.handleTokenEvent('Token expired');
    };

    private forceRelogin = () => {
        console.log('Forcing relogin due to token renewal failure');

        const targetPath = window.location.pathname + window.location.search;

        // If we're already on login page, try to extract original returnUrl
        if (window.location.pathname.startsWith(loginPathPrefix)) {
            const params = new URLSearchParams(window.location.search);
            const existingReturnUrl = params.get('returnUrl');

            if (existingReturnUrl) {
                // Use existing returnUrl
                window.location.href = `/login?returnUrl=${existingReturnUrl}`;
                return;
            } else {
                // If no returnUrl, just go to clean login
                window.location.href = loginPathPrefix;
                return;
            }
        }

        // If we're NOT on login page, create new returnUrl
        const returnUrl = encodeURIComponent(targetPath);
        window.location.href = `/login?returnUrl=${returnUrl}`;
    };

    render(): JSX.Element {
        return (
            <Provider store={store}>
                <Router>
                    <Switch>
                        <Route path={loginPathPrefix} component={Login} />
                        <Route path={loggingInPathPrefix} component={LoggingIn} />
                        <Route path={logoutPathPrefix} component={LogOut} />
                        <PrivateRoute>
                            <Container fluid>
                                <TransitIqSidebar user={this.state.currentUser} />
                                <Container fluid className="main">
                                    <Container fluid className="toolbar">
                                        <UserMenu />
                                    </Container>
                                    <Container fluid>
                                        <Route exact path="/">
                                            <Redirect to={defaultRoutePath} />
                                        </Route>
                                        {groupedRoutes.map(route => (
                                            <TransitIqRootRoute key={route.label} {...route} />
                                        ))}
                                    </Container>
                                </Container>
                            </Container>
                        </PrivateRoute>
                        <Route path="*">
                            <h3>404</h3>
                        </Route>
                    </Switch>
                </Router>
            </Provider>
        );
    }
}

function App() {
    return (
        <AppInternal />
    );
}

export default App;
